import React, { useState } from 'react';
import Button from '../../components/atoms/Button';
import Input from '../../components/atoms/Input';
import GoogleSignInButton from '../../components/atoms/GoogleSignInButton';
import './AdminSignup.css';

interface AdminSignupProps {
  onSignup: (email: string, password: string, confirmPassword: string, fullName: string) => void;
  onGoogleSignIn: () => void;
  onBackToLogin: () => void;
  isLoading?: boolean;
  error?: string;
}

const AdminSignup: React.FC<AdminSignupProps> = ({ 
  onSignup, 
  onGoogleSignIn,
  onBackToLogin, 
  isLoading = false, 
  error 
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [formErrors, setFormErrors] = useState<{ 
    email?: string; 
    password?: string; 
    confirmPassword?: string;
    fullName?: string;
  }>({});

  const validateForm = () => {
    const errors: { 
      email?: string; 
      password?: string; 
      confirmPassword?: string;
      fullName?: string;
    } = {};

    if (!fullName.trim()) {
      errors.fullName = 'Full name is required';
    } else if (fullName.trim().length < 2) {
      errors.fullName = 'Full name must be at least 2 characters';
    }

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSignup(email, password, confirmPassword, fullName);
    }
  };

  return (
    <div className="admin-signup">
      <div className="admin-signup-container">
        <div className="admin-signup-header">
          <a href="/" className="admin-logo-link" aria-label="Go to homepage">
            <img src="/images/logo/tfr.avif" alt="The Frosting Room" className="admin-logo" />
          </a>
          <h1>Admin Signup</h1>
          <p>Create your admin account to manage The Frosting Room.</p>
        </div>

        <form onSubmit={handleSubmit} className="admin-signup-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="fullName">Full Name</label>
            <Input
              type="text"
              id="fullName"
              placeholder="Enter your full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={formErrors.fullName ? 'error' : ''}
              required
            />
            {formErrors.fullName && (
              <span className="field-error">{formErrors.fullName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <Input
              type="email"
              id="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={formErrors.email ? 'error' : ''}
              required
            />
            {formErrors.email && (
              <span className="field-error">{formErrors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <Input
              type="password"
              id="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={formErrors.password ? 'error' : ''}
              required
            />
            {formErrors.password && (
              <span className="field-error">{formErrors.password}</span>
            )}
            <div className="password-requirements">
              <small>Password must be at least 8 characters with uppercase, lowercase, and number</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <Input
              type="password"
              id="confirmPassword"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={formErrors.confirmPassword ? 'error' : ''}
              required
            />
            {formErrors.confirmPassword && (
              <span className="field-error">{formErrors.confirmPassword}</span>
            )}
          </div>

          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              size="large"
              disabled={isLoading}
              className="signup-button"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </div>

          <div className="divider">
            <span>or</span>
          </div>

          <div className="google-signin-section">
            <GoogleSignInButton
              onClick={onGoogleSignIn}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Continue with Google
            </GoogleSignInButton>
          </div>
        </form>

        <div className="admin-signup-footer">
          <p>Already have an account? 
            <button 
              type="button" 
              onClick={onBackToLogin}
              className="login-link"
            >
              Sign in here
            </button>
          </p>
          <p className="copyright">© 2024 The Frosting Room. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminSignup;
